import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MetricsCard from './components/MetricsCard';
import QuickActionCard from './components/QuickActionCard';
import ActivityFeed from './components/ActivityFeed';
import SystemHealth from './components/SystemHealth';
import UserManagement from './components/UserManagement';
import { adminService, getAdminClient } from '../../services/adminService';
import { supabase } from '../../lib/supabase';
import { formatNumber } from '../../utils/formatNumber';

export default function AdminMasterDashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [metricsData, bookingsData] = await Promise.all([
        adminService?.getDashboardMetrics(),
        loadBookings()
      ]);
      setMetrics(metricsData);
    } catch (err) {
      setError(err?.message || 'فشل تحميل بيانات لوحة التحكم');
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    const client = getAdminClient() || supabase;
    try {
      const { data: bookingsData, error: bookingsError } = await client
        .from("bookings")
        .select(`
          id,
          created_at,
          updated_at,
          status,
          city,
          photography_type,
          price,
          final_price_sar,
          customer_id,
          photographer_id,
          assigned_photographer_id,
          booking_date,
          booking_time,
          scheduled_at,
          duration,
          location,
          location_details,
          pricing_snapshot,
          attempt_count,
          hold_expires_at,
          payment_expires_at,
          payment_transaction_id,
          photographer_response_at
        `)
        .order("created_at", { ascending: false })
        .limit(50);
      
      if (bookingsError) {
        console.error('❌ Error loading bookings:', bookingsError);
        return [];
      }
      
      // Enrich bookings with customer and photographer info
      if (bookingsData && bookingsData.length > 0) {
        const customerIds = [...new Set(bookingsData.map(b => b.customer_id).filter(Boolean))];
        const photographerIds = [...new Set([
          ...bookingsData.map(b => b.photographer_id).filter(Boolean),
          ...bookingsData.map(b => b.assigned_photographer_id).filter(Boolean)
        ].filter(Boolean))];
        
        const [customersResult, photographersResult] = await Promise.all([
          customerIds.length > 0 
            ? client.from('user_profiles').select('id, full_name, email, phone, avatar_url').in('id', customerIds)
            : Promise.resolve({ data: [], error: null }),
          photographerIds.length > 0
            ? client.from('user_profiles').select('id, full_name, email, phone, avatar_url').in('id', photographerIds)
            : Promise.resolve({ data: [], error: null })
        ]);
        
        const customersMap = {};
        const photographersMap = {};
        
        if (customersResult.data) {
          customersResult.data.forEach(c => { customersMap[c.id] = c; });
        }
        if (photographersResult.data) {
          photographersResult.data.forEach(p => { photographersMap[p.id] = p; });
        }
        
        const enrichedBookings = bookingsData.map(booking => ({
          ...booking,
          customer: customersMap[booking.customer_id] || null,
          photographer: photographersMap[booking.photographer_id || booking.assigned_photographer_id] || null
        }));
        
        setBookings(enrichedBookings);
        return enrichedBookings;
      }
      
      setBookings([]);
      return [];
    } catch (err) {
      console.error('Failed to load bookings:', err);
      return [];
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_photographer_confirmation: { label: 'في انتظار التأكيد', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      awaiting_payment: { label: 'في انتظار الدفع', color: 'bg-blue-100 text-blue-700 border-blue-300' },
      paid_confirmed: { label: 'مؤكد ومدفوع', color: 'bg-green-100 text-green-700 border-green-300' },
      completed: { label: 'مكتمل', color: 'bg-green-100 text-green-700 border-green-300' },
      declined: { label: 'مرفوض', color: 'bg-red-100 text-red-700 border-red-300' },
      expired: { label: 'منتهي', color: 'bg-gray-100 text-gray-700 border-gray-300' },
      cancelled_by_customer: { label: 'ملغي من العميل', color: 'bg-red-100 text-red-700 border-red-300' },
      cancelled_by_admin: { label: 'ملغي من المشرف', color: 'bg-red-100 text-red-700 border-red-300' },
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700 border-gray-300' };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>جاري تحميل لوحة التحكم...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="AlertCircle" size={24} color="#ef4444" />
            <h3 className="text-lg font-semibold text-red-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>خطأ</h3>
          </div>
          <p className="text-red-800" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 py-8 px-4" dir="rtl">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-700 to-blue-600 bg-clip-text text-transparent mb-2" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                لوحة التحكم الرئيسية
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                مرحباً {userProfile?.role === 'admin' ? (userProfile?.full_name || 'المشرف') : 'المشرف'} - التحكم المركزي في المنصة
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-green-100 text-green-800 rounded-lg flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>النظام يعمل</span>
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricsCard
            title="إجمالي المستخدمين"
            value={metrics?.totalUsers || 0}
            icon="Users"
            iconColor="purple"
            color="purple"
            trend="+12%"
            subtitle="المستخدمون المسجلون"
          />
          <MetricsCard
            title="الحجوزات النشطة"
            value={metrics?.activeBookings || 0}
            icon="Calendar"
            iconColor="pink"
            color="pink"
            trend="+8%"
            subtitle="الحجوزات الحالية"
          />
          <MetricsCard
            title="الإيرادات (ريال)"
            value={metrics?.totalRevenue || 0}
            icon="DollarSign"
            iconColor="cyan"
            color="cyan"
            trend="+15%"
            subtitle="إجمالي الإيرادات"
          />
          <MetricsCard
            title="صحة النظام"
            value="98%"
            icon="Activity"
            iconColor="emerald"
            color="emerald"
            trend="مستقر"
            subtitle="حالة النظام"
          />
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
            إجراءات سريعة
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionCard
              title="إدارة المستخدمين"
              icon="UserCog"
              color="purple"
              onClick={() => console.log('User Management')}
            />
            <QuickActionCard
              title="مراقبة الحجوزات"
              icon="Calendar"
              color="pink"
              onClick={() => console.log('Booking Monitoring')}
            />
            <QuickActionCard
              title="التقارير المالية"
              icon="FileText"
              color="cyan"
              onClick={() => console.log('Financial Reports')}
            />
            <QuickActionCard
              title="إعدادات النظام"
              icon="Settings"
              color="slate"
              onClick={() => console.log('System Settings')}
            />
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Management Panel */}
          <div className="lg:col-span-2">
            <UserManagement />
          </div>

          {/* Activity Feed */}
          <div>
            <ActivityFeed />
          </div>
        </div>

        {/* Recent Bookings Section */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
              آخر الحجوزات
            </h2>
            <button
              onClick={() => navigate('/admin?tab=bookings')}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 text-sm font-medium"
              style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
            >
              <Icon name="ArrowLeft" size={16} color="white" />
              <span>عرض الكل</span>
            </button>
          </div>

          {bookings.length === 0 ? (
            <div className="text-center py-8">
              <Icon name="CalendarX" size={48} color="#cbd5e1" className="mx-auto mb-2" />
              <p className="text-slate-500" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>لا توجد حجوزات حديثة</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {bookings.slice(0, 10).map((booking) => {
                const finalPrice = booking.final_price_sar || booking.price || 0;
                const bookingDate = booking.booking_date || booking.scheduled_at;
                
                return (
                  <div
                    key={booking.id}
                    onClick={() => navigate(`/admin?tab=bookings`)}
                    className="p-4 rounded-lg border border-slate-200 hover:bg-slate-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-bold text-primary font-mono">
                            #{String(booking.id).slice(0, 8).toUpperCase()}
                          </span>
                          {getStatusBadge(booking.status)}
                        </div>
                        <div className="text-sm font-semibold text-slate-900 mb-1">
                          {booking.photography_type || "نوع غير محدد"}
                        </div>
                        <div className="text-xs text-slate-600 space-y-0.5">
                          <div className="flex items-center gap-2">
                            <Icon name="MapPin" size={12} color="#64748b" />
                            <span>{booking.city || "—"}</span>
                          </div>
                          {booking.customer && (
                            <div className="flex items-center gap-2">
                              <Icon name="User" size={12} color="#64748b" />
                              <span>{booking.customer.full_name || "عميل"}</span>
                            </div>
                          )}
                          {booking.photographer && (
                            <div className="flex items-center gap-2">
                              <Icon name="Camera" size={12} color="#64748b" />
                              <span>{booking.photographer.full_name || "مصور"}</span>
                            </div>
                          )}
                          {bookingDate && (
                            <div className="flex items-center gap-2">
                              <Icon name="Calendar" size={12} color="#64748b" />
                              <span>{new Date(bookingDate).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                              {booking.booking_time && <span>• {booking.booking_time}</span>}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Icon name="Clock" size={12} color="#64748b" />
                            <span>{new Date(booking.created_at).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-lg text-sm font-bold">
                          {formatNumber(finalPrice)} ر.س
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* System Health */}
        <SystemHealth />
      </div>
    </div>
  );
}